/**
 * HelpMenuSubMenu — sous-menu « Aide & contact » d'un DropdownMenu.
 *
 * @remarks
 * Anciennement un menu autonome (`HelpMenu`, bouton ❓ dédié, SAAS-640). Fusionné
 * dans le menu compte ({@link UserMenu} desktop / {@link MobileAccountMenu} mobile)
 * sous forme de sous-menu : un item déclencheur « Aide & contact » ouvre un
 * `SubContent` listant les liens Discord (nouvel onglet). À insérer comme un item
 * du menu parent.
 *
 * Sur mobile, un sous-menu latéral n'a pas la place de s'ouvrir (il sort de
 * l'écran) : {@link MobileAccountMenu} affiche {@link HelpMenuItems} à la place
 * du menu principal, sans sous-menu.
 */

import {
  Bug,
  ChevronRight,
  LifeBuoy,
  MessageCircleQuestion,
  Users,
  type LucideIcon,
} from 'lucide-react';
import {
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  Icon,
} from '@/shared/ui';

interface HelpItem {
  icon: LucideIcon;
  title: string;
  description: string;
  href: string;
}

/**
 * Liens vers les salons Discord (préco Emilie, SAAS-640 ; cibles actualisées SAAS-1070).
 *
 * @remarks
 * Les trois premiers sont des liens de salon (`discord.com/channels/…`) : ils ne
 * s'ouvrent correctement que pour un utilisateur déjà membre du serveur.
 * « Communauté Alt » pointe désormais sur un lien d'invitation (`discord.gg/…`),
 * qui couvre aussi les non-membres — le swap se limite à ces constantes.
 */
const HELP_ITEMS: readonly HelpItem[] = [
  {
    icon: MessageCircleQuestion,
    title: 'Une question sur le test',
    description: 'Envoyez une question sur le contenu des évaluations techniques',
    href: 'https://discord.com/channels/1503726529753124874/1550112550593630369',
  },
  {
    icon: Bug,
    title: 'Un bug à signaler',
    description: 'Ouvre un ticket pour signaler un bug technique sur la plateforme',
    href: 'https://discord.com/channels/1503726529753124874/1550112955096371250',
  },
  {
    icon: LifeBuoy,
    title: "Besoin d'aide ?",
    description: 'Envoyez une demande auprès de notre équipe',
    href: 'https://discord.com/channels/1503726529753124874/1550113411076071466',
  },
  {
    icon: Users,
    title: 'Communauté Alt',
    description: 'Rejoignez la communauté pour échanger avec d’autres développeurs',
    href: 'https://discord.gg/BVqM2FURaY',
  },
] as const;

/** Liens d'aide (Discord), à insérer dans un menu ou un sous-menu. */
export function HelpMenuItems() {
  return (
    <>
      {HELP_ITEMS.map(({ icon: ItemIcon, title, description, href }) => (
        <DropdownMenuItem key={href} asChild className="items-start">
          <a href={href} target="_blank" rel="noopener noreferrer">
            <Icon icon={ItemIcon} size="lg" className="text-fg-muted mt-0.5" />
            <span className="flex flex-col gap-0.5">
              <span className="text-fg font-medium">{title}</span>
              <span className="text-fg-muted text-xs">{description}</span>
            </span>
          </a>
        </DropdownMenuItem>
      ))}
    </>
  );
}

export function HelpMenuSubMenu() {
  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger>
        <Icon icon={LifeBuoy} size="md" />
        <span>Aide &amp; contact</span>
        <Icon icon={ChevronRight} size="md" className="text-fg-muted ml-auto" />
      </DropdownMenuSubTrigger>
      <DropdownMenuSubContent className="w-80">
        <HelpMenuItems />
      </DropdownMenuSubContent>
    </DropdownMenuSub>
  );
}
